import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import {
    Button, Text,
} from 'reaxl';

import {
    kbbVRSDataDuck,
    queryDuck,
} from '@/ducks';

import {
    bapActiveModelDuck,
    bapActiveOwnerDuck,
    bapSelectedOptionsDuck,
} from '@/ducks/bap';

import useBapNavigation from '@/hooks/useBapNavigation';

import BuildAndPriceBodyStyleTab from './BuildAndPriceBodyStyleTab';

const BUILD_PRICE_BODY_STYLES = {
    Sedan: [],
    SUV: [],
    Truck: [],
    Coupe: [],
    Convertible: [],
    Wagon: [],
    Van: [],
    Hatchback: [],
};

const mapSeriesCodeToModelCode = (vrsData, makeCode, seriesCode) => {
    let modelCodeMapping = '';
    const vrsModels = Object.entries(_get(vrsData, `${makeCode}`, {}));
    Object.values(vrsModels).forEach(([, value]) => {
        const firstSeriesName = Object.values(value)?.[0];
        const seriesName = firstSeriesName?.model?.replace(' ', '_')?.replace('-', '_')?.toUpperCase();

        if (seriesCode === seriesName) {
            modelCodeMapping = firstSeriesName?.atcModelCode;
        }
    });
    return modelCodeMapping;
};

function BuildAndPriceBodyStyleContainer() {
    const dispatch = useDispatch();
    const navigateToBap = useBapNavigation();
    const activeModel = useSelector(bapActiveModelDuck.selectors.getDuckState);
    const activeOwnerId = useSelector(bapActiveOwnerDuck.selectors.getDuckState);
    const query = useSelector(queryDuck.selectors.getDuckState);
    const vrsData = useSelector(kbbVRSDataDuck.selectors.getData);
    const currentYear = new Date().getFullYear();
    const queryYear = query?.year && Number(query?.year);

    const [currentQueryYear, setCurrentQueryYear] = useState(queryYear);

    const makeCodeList = query?.makeCode || query?.makeCodeList;
    const makeCode = !Array.isArray(makeCodeList) && makeCodeList;

    const modelCodeList = query?.modelCode || query?.modelCodeList;
    const modelCodeQuery = !Array.isArray(modelCodeList) && modelCodeList;

    const seriesCodeList = query?.seriesCode || query?.seriesCodeList;
    const seriesCode = !Array.isArray(seriesCodeList) && seriesCodeList;

    let modelCode = modelCodeQuery;

    if (seriesCode && !modelCodeQuery) {
        modelCode = mapSeriesCodeToModelCode(vrsData, makeCode, seriesCode);
    }

    const defaultTab = Object.keys(BUILD_PRICE_BODY_STYLES)[0];
    const [activeStyleTab, setActiveStyleTab] = useState(defaultTab);

    const [interactedChangeModelLink, setChangeModelLink] = useState(false);
    const initialModelStyle = Object.values(_get(vrsData, `${makeCode}.${modelCode}`, []))?.[0]?.bodyStyle;

    const shouldRenderAllModels = makeCode && !_isEmpty(vrsData) && vrsData?.[makeCode];

    useEffect(() => {
        if (makeCode) {
            dispatch(kbbVRSDataDuck.creators.loadBodyStyles({ makeCode, year: queryYear }));
        }
    }, [dispatch, makeCode, queryYear]);

    useEffect(() => {
        if ((queryYear !== currentQueryYear) && activeModel) {
            const defaultModel = modelCode || activeModel;
            setCurrentQueryYear(queryYear);

            dispatch(kbbVRSDataDuck.creators.loadVrsDataWithModels({
                makeCode,
                selectedModel: defaultModel,
                selectedYear: queryYear,
            }));
        }
    }, [dispatch, queryYear, currentQueryYear, activeModel, makeCode, modelCode]);

    const handlePreviousYearClick = useCallback((previousYear) => {
        navigateToBap({
            makeCode,
            modelCode,
            ownerId: activeOwnerId || query?.ownerId,
            useReferrer: false,
            year: previousYear,
            zip: query?.zip,
        });
        dispatch(bapSelectedOptionsDuck.creators.reset());
    }, [dispatch, navigateToBap, activeOwnerId, makeCode, modelCode, query]);

    const loadMoreModelsLink = () => !interactedChangeModelLink
        && activeStyleTab === initialModelStyle && (
        <div className="margin-left-5 margin-right-4 margin-vertical-4">
            <Button
                data-cmp="lnk-chng-mdl"
                onClick={() => setChangeModelLink(true)}
                bsStyle="text"
            >
                Change Model
            </Button>
        </div>
    );

    const previousYearLink = () => {
        const isCurrentYearOrNewer = queryYear && (queryYear === currentYear || queryYear === currentYear + 1);
        const previousYear = queryYear ? queryYear - 1 : currentYear - 1;

        return (
            (isCurrentYearOrNewer || !queryYear)
            && (interactedChangeModelLink || activeStyleTab !== initialModelStyle) && (
                <div className="margin-left-5 margin-right-4 margin-vertical-4">
                    <Text
                        weight="bold"
                        componentClass="div"
                    >
                        Not finding the model you&apos;re looking for?
                    </Text>
                    <Button
                        data-cmp="lnk-prv-yr"
                        onClick={() => handlePreviousYearClick(previousYear)}
                        bsStyle="text"
                    >
                        Check models from the previous year.
                    </Button>
                </div>
            )
        );
    };

    return shouldRenderAllModels ? (
        <>
            <BuildAndPriceBodyStyleTab
                initialModelStyle={initialModelStyle}
                interactedChangeModelLink={interactedChangeModelLink}
                setActiveStyleTab={setActiveStyleTab}
                modelCode={modelCode}
            />
            {loadMoreModelsLink()}
            {previousYearLink()}
        </>
    ) : null;
}

export default BuildAndPriceBodyStyleContainer;
